import { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import SoursSelector from 'components/SoursSelector/SoursSelector';
import TextField from 'components/TextField/TextField';
import {
  ADDRESS_COUNTRY,
  COMPANY_NAME, PROFIT,
  PURCHASE_VOLUME,
  SECTOR, SIZE_MAX, STOCK_TICKER,
  SUPPLIERS_EXPENSES, WEBSITE,
} from 'constants/formFields';
import { DEFAULT_OPTION } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useCompanyCompetitor from 'hooks/useCompanyCompetitor';
import useGeoLocations from 'hooks/useGeoLocations';

import useUpdateItemForm from './hooks/useUpdateItemForm';

import type IFieldError from 'store/types/IFieldError';
import type { TUpdateAnalysisSchema } from 'utils/validators';
import type IUpdateItem from './interfaces/IUpdateItem';

import styles from './scss/UpdateItem.module.scss';

function UpdateItem({
  open, onClose, title, onUpdateItem, isPageLoading, selectedItem, locationsCountries,
}: IUpdateItem) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useUpdateItemForm({ isPageLoading });

  const {
    onFindCompaniesCompetitor,
    onChangeCompany,
    companyDetails,
    loadingDetails,
  } = useCompanyCompetitor<TUpdateAnalysisSchema>({ watch, setValue });

  const {
    onFindCountries, onChangeCountry, defaultCountries,
  } = useGeoLocations<TUpdateAnalysisSchema>({ watch, setValue });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onUpdateItemSubmit = handleSubmit((data) => {
    const body = {
      countryCode: data[ADDRESS_COUNTRY]?.value || '',
      stockTickers: data[STOCK_TICKER]?.map((item) => item.value) || [],
      website: data[WEBSITE] || '',
      sectors: data[SECTOR]?.map((item) => item.value) || [],
      employeeCount: Number(data[SIZE_MAX]) || 0,
      supplierExpenses: data[SUPPLIERS_EXPENSES] || '',
      customerPurchases: data[PURCHASE_VOLUME] || '',
      revenue: data[PROFIT] || '',
    };
    onUpdateItem(body);
    onCancel();
  });

  const stockTickerOptions = () => (companyDetails ? companyDetails?.stockTickers?.map((item) => {
    const stockItem = { label: item, value: item };
    return stockItem.label && stockItem.value ? stockItem : DEFAULT_OPTION;
  }) : [DEFAULT_OPTION]);

  const sectors = () => (companyDetails ? companyDetails?.sectors?.map((item) => {
    const sector = { label: item, value: item };
    return sector.label && sector.value ? sector : DEFAULT_OPTION;
  }) : [DEFAULT_OPTION]);

  useEffect(() => {
    if (selectedItem) {
      setValue(COMPANY_NAME, {
        label: selectedItem?.stakeholder?.name || selectedItem?.vcmStakeholder?.name || '',
        value: selectedItem?.stakeholder?.name || selectedItem?.vcmStakeholder?.name || '',
      });
      setValue(STOCK_TICKER, selectedItem.stockTickers.map((ticker) => ({ label: ticker, value: ticker })));
      setValue(WEBSITE, selectedItem.website);
      setValue(SECTOR, selectedItem.sectors.map((sector) => ({ label: sector, value: sector })));
      setValue(SIZE_MAX, selectedItem.employeeCount ? `${selectedItem.employeeCount}` : '');
      setValue(SUPPLIERS_EXPENSES, selectedItem.supplierExpenses ? `${selectedItem.supplierExpenses}` : '');
      const country = locationsCountries
        ?.find((countryItem) => countryItem.iso2 === selectedItem.countryCode);
      setValue(ADDRESS_COUNTRY, {
        label: country?.name || selectedItem.countryCode,
        value: country?.iso2 || selectedItem.countryCode,
      });
      setValue(PURCHASE_VOLUME, selectedItem.customerPurchases ? `${selectedItem.customerPurchases}` : '');
      setValue(PROFIT, selectedItem.revenue ? `${selectedItem.revenue}` : '');
    }
  }, [selectedItem, setValue, locationsCountries]);

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
      showLoader={loadingDetails}
    >
      <div className={styles.root}>
        <div className={styles.formWrapper} ref={modalRef}>
          <Controller
            control={control}
            name={COMPANY_NAME}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.companyName')}
                placeholder={t('materialityAssessment.placeholder.searchCompany')}
                value={value?.value ? value : null}
                onChange={(newValue) => {
                  onChange(newValue);
                  onChangeCompany();
                }}
                error={error?.message}
                loadOptions={onFindCompaniesCompetitor}
                isSearchable
                isAsync
                isClearable
                menuPosition="absolute"
                isDisabled
                required
              />
            )}
          />
          <Controller
            control={control}
            name={STOCK_TICKER}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <SoursSelector
                label={t('materialityAssessment.fields.stockTicker')}
                placeholder={t('materialityAssessment.placeholder.selectTicker')}
                titleModal={t('materialityAssessment.addCustomStockTicker')}
                titleField={t('materialityAssessment.fields.stockTickerName')}
                titleSecondField={t('materialityAssessment.fields.stockTickerValue')}
                options={stockTickerOptions()}
                onChange={onChange}
                value={value || []}
                error={(error as IFieldError)?.message}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={ADDRESS_COUNTRY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.country')}
                placeholder={t('materialityAssessment.placeholder.selectCountry')}
                value={value?.value ? value : null}
                onChange={(newValue) => {
                  onChange(newValue);
                  onChangeCountry();
                }}
                error={error?.message}
                loadOptions={onFindCountries}
                defaultOptions={defaultCountries}
                isSearchable
                isAsync
                isClearable
                className={styles.asyncSelect}
                menuPosition="absolute"
                isDisabled={loadingDetails}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={WEBSITE}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.website')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                disabled={loadingDetails}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SECTOR}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <SoursSelector
                label={t('materialityAssessment.fields.sector')}
                placeholder={t('materialityAssessment.placeholder.selectSector')}
                titleModal={t('materialityAssessment.addCustomSector')}
                titleField={t('materialityAssessment.fields.sectorName')}
                titleSecondField={t('materialityAssessment.fields.sectorValue')}
                options={sectors()}
                onChange={onChange}
                value={value || []}
                error={(error as IFieldError)?.message}
                required
              />
            )}
          />
          <div className={styles.sizeFields}>
            <Controller
              control={control}
              name={SIZE_MAX}
              render={({
                field: {
                  name,
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <NumberField
                  name={name}
                  value={value}
                  label={t('materialityAssessment.fields.size')}
                  onChange={(values) => onChange(values.value)}
                  decimalScale={0}
                  placeholder={t('materialityAssessment.placeholder.max')}
                  allowNegative={false}
                  disabled={loadingDetails}
                  error={(error as IFieldError)?.message}
                  required
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name={SUPPLIERS_EXPENSES}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <NumberField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.supplierExpenses')}
                onChange={(values) => onChange(values.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                error={(error as IFieldError)?.message}
                allowNegative={false}
                disabled={loadingDetails}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={PURCHASE_VOLUME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <NumberField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.purchaseVolume')}
                onChange={(values) => onChange(values.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                error={(error as IFieldError)?.message}
                allowNegative={false}
                disabled={loadingDetails}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={PROFIT}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <NumberField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.profit')}
                onChange={(values) => onChange(values.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                error={(error as IFieldError)?.message}
                allowNegative={false}
                disabled={loadingDetails}
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onUpdateItemSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default UpdateItem;
